"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Image, { type StaticImageData } from "next/image";
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import { RENTAL_VEHICLES } from "@/data/vehicles";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { LANDING, href } from "./landingDict";

type VehicleImage = {
  src: StaticImageData;
  alt: string;
};

const images: VehicleImage[] = [
  ...RENTAL_VEHICLES.map((vehicle) => ({
    src: vehicle.images.main,
    alt: vehicle.name,
  })),
];

type VehicleGallerySectionProps = {
  locale?: Locale;
};

export function VehicleGallerySection({
  locale = "fr",
}: VehicleGallerySectionProps) {
  const t = tr(LANDING, locale).gallery;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (isPaused) return;

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 5));
    }, 1000);

    const slideInterval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setCountdown(5);
    }, 5000);

    return () => {
      clearInterval(countdownInterval);
      clearInterval(slideInterval);
    };
  }, [isPaused]);

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 1000 : -1000, opacity: 0 }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (dir: number) => ({
      zIndex: 0,
      x: dir < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  } as const;

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) =>
    Math.abs(offset) * velocity;

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(
      (prev) => (prev + newDirection + images.length) % images.length,
    );
    setCountdown(5);
  };

  const togglePause = () => {
    setIsPaused((p) => !p);
    setCountdown(5);
  };

  return (
    <section className="w-full bg-white py-28 text-black">
      <div className="mx-auto grid max-w-[90rem] grid-cols-1 items-center gap-10 px-6 md:px-8 lg:grid-cols-2 lg:gap-16">
        {/* Bloc visuel (carré) */}
        <motion.div
          className="group relative aspect-square w-full overflow-hidden rounded-2xl"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          {/* Décor angles */}
          <div className="pointer-events-none absolute left-4 top-4 z-10 h-16 w-16 border-s-2 border-t-2 border-primary/40" />
          <div className="pointer-events-none absolute bottom-4 right-4 z-10 h-16 w-16 border-b-2 border-e-2 border-primary/40" />

          <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
            <Icon icon="ph:car" className="h-20 w-20 text-primary/60" />
          </div>

          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(_e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) paginate(1);
                else if (swipe > swipeConfidenceThreshold) paginate(-1);
              }}
              className="absolute inset-0"
            >
              <Image
                src={images[currentIndex].src}
                alt={images[currentIndex].alt}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                loading={currentIndex === 0 ? "eager" : "lazy"}
              />
            </motion.div>
          </AnimatePresence>

          {/* Overlay dégradé léger */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Contrôles */}
          <div className="absolute inset-0 z-20 transition-opacity duration-300">
            <motion.button
              initial={false}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.2 }}
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-3 text-primary backdrop-blur-sm hover:bg-black/60"
              onClick={() => paginate(-1)}
              title={t.prevLabel}
              aria-label={t.prevLabel}
              type="button"
            >
              <Icon icon="ph:caret-left-thin" className="h-5 w-5" />
            </motion.button>
            <motion.button
              initial={false}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.2 }}
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-3 text-primary backdrop-blur-sm hover:bg-black/60"
              onClick={() => paginate(1)}
              title={t.nextLabel}
              aria-label={t.nextLabel}
              type="button"
            >
              <Icon icon="ph:caret-right-thin" className="h-5 w-5" />
            </motion.button>

            {/* Bouton Pause/Play */}
            <motion.button
              initial={false}
              animate={{ opacity: isHovered || isPaused ? 1 : 0 }}
              transition={{ duration: 0.2 }}
              onClick={togglePause}
              className="absolute right-4 top-4 rounded-full bg-black/40 p-3 text-primary backdrop-blur-sm hover:bg-black/60"
              title={isPaused ? t.resumeLabel : t.pauseLabel}
              aria-label={isPaused ? t.resumeLabel : t.pauseLabel}
              type="button"
            >
              <Icon
                icon={isPaused ? "ph:play-thin" : "ph:pause-thin"}
                className="h-5 w-5"
              />
            </motion.button>
          </div>

          {/* Indicateurs */}
          <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {images.map((_img, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                  setCountdown(5);
                }}
                title={t.dotLabel(index + 1)}
                aria-label={t.dotLabel(index + 1)}
                className={`h-1.5 rounded-full transition-all ${index === currentIndex ? "w-6 bg-primary" : "w-1.5 bg-black/40 hover:bg-black/60"}`}
                type="button"
              />
            ))}
          </div>

          {/* Countdown */}
          <div className="absolute bottom-1 left-1/2 z-20 -translate-x-1/2 font-space-age text-primary">
            {!isPaused && (
              <motion.span
                key={countdown}
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {countdown}s
              </motion.span>
            )}
          </div>
        </motion.div>

        {/* Texte côté */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          className="px-1"
        >
          <p className="mb-3 text-sm uppercase tracking-[0.35em] text-black/60">
            {t.eyebrow}
          </p>
          <h2 className="mb-4 font-space-age text-2xl tracking-[0.25em] md:text-3xl">
            {t.heading}
          </h2>
          <p className="mb-6 max-w-xl text-black/75">{t.intro}</p>
          <ul className="mb-8 grid grid-cols-2 gap-3 text-sm text-black/80">
            {t.bullets.map((item) => (
              <li key={item} className="inline-flex items-center gap-2">
                <Icon icon="mdi:check" className="h-5 w-5 text-primary" />{" "}
                {item}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3">
            <Link
              href={href(locale, "/location/vehicules")}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 font-semibold text-white transition-colors hover:bg-gold-600"
            >
              {t.ctaPrimary}
              <Icon icon="mdi:arrow-right" />
            </Link>
            <Link
              href={href(locale, "/contact")}
              className="inline-flex items-center gap-2 rounded-lg border border-black/10 px-5 py-2 text-black/90 transition-colors hover:border-primary"
            >
              {t.ctaSecondary}
              <Icon icon="mdi:file-document-edit-outline" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
